import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Button, Grid, Typography } from "@material-ui/core";

// NPM
import { useTrail, animated, config } from "react-spring";

// Components
import StyledSectionContainer from "../components/common/SectionContainer.Component";
import { AnimatedMedicareCardContainer } from "../components/healthCoverDetails/MedicareCardContainer.Component";
import { AnimatedConcessionCardContainer } from "../components/healthCoverDetails/ConcessionCardContainer.Component";
import LoadingButton from "../components/common/LoadingButton.Component";
import GenericDialogV2 from "../components/common/GenericDialogV2.Component";

// Classes
import Utility from "../domain/classes/Utility";
import IAppointmentBookingRequest from "../domain/interfaces/IAppointmentBookingRequest";
import IAppointmentBookingResponse from "../domain/interfaces/IAppointmentBookingResponse";
import { useCookies } from "react-cookie";
import { useApplicationManager } from "../contexts/ApplicationManager.Context";
import AppointmentBookingRequest from "../domain/classes/AppointmentBookingRequest";
import UnableToProcessBookingStageOneError from "../domain/classes/errors/UnableToProcessBookingStageOne";
import Journey from "../domain/classes/Journey";
import { InitialMedicareCardErrorState } from "../domain/interfaces/IMedicareCardError";
import { InitialConcessionCardErrorState } from "../domain/interfaces/IConcessionCardError";

// Config
import {
	QV_MEDICAL_PHONE_NUMBER,
	SAD_COLOURED,
} from "../domain/utility/config";


export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		imgContainer: { height: 80, marginBottom: theme.spacing(2) },
		img: { height: "100%", margin: "auto", display: "block" },
	});


export interface HealthCoverDetailsSectionProps {}

const HealthCoverDetailsSection = React.forwardRef<
	HTMLDivElement,
	HealthCoverDetailsSectionProps & WithStyles<typeof styles>
>(({ classes }, ref) => {
	const components = [
		AnimatedMedicareCardContainer,
		AnimatedConcessionCardContainer,
	];
	const [cookies, setCookies] = useCookies();
	const {
		patientDetails,
		appointmentBookingRequest,
		selectedApptType,
		selectedDoctor,
		handleAppointmentBookingRequestChange,
		handleJourneyStageChange,
		journeyStage,
		medicareCard,
		concessionCard,
		medicareCardError,
		concessionCardError,
		pushDialog
	} = useApplicationManager();


	const trail = useTrail(components.length, {
		config: config.wobbly,
		opacity: 1,
		x: 0,
		from: { opacity: 0, x: -50 },
	});

	const isValidationFailed = () => {
		let hasMedicareError = false;
		let hasConcessionError = false;

		if (patientDetails.hasMedicare) {
			hasMedicareError =
				JSON.stringify(medicareCardError) !==
				JSON.stringify(InitialMedicareCardErrorState);
		}

		if (patientDetails.hasConcession) {
			hasConcessionError =
				JSON.stringify(concessionCardError) !==
				JSON.stringify(InitialConcessionCardErrorState);
		}

		return hasMedicareError || hasConcessionError;
	};

	const setBookingCookies = (
		data: IAppointmentBookingResponse,
		apptRequest: IAppointmentBookingRequest
	) => {
		// console.log(data);
		setCookies("stage_id", data.stageId, {
			maxAge: 900,
		});

		setCookies("appointment_request", JSON.stringify(apptRequest), {
			maxAge: 900,
		});
		setCookies("appointment_type", JSON.stringify(selectedApptType), {
			maxAge: 900,
		});

		setCookies("doctor", JSON.stringify(selectedDoctor), {
			maxAge: 900,
		});
	};

	const [loading, setLoading] = React.useState<boolean>(false);
    //const [error, setError] = React.useState<string>("");

	const pushErrorDialog = (errorMessage: string) => {
		pushDialog(
			<GenericDialogV2
				actionStyle="Alert"
				title="An error has occurred."
				content={
					<React.Fragment>
						<div className={classes.imgContainer}>
							<img
								src={SAD_COLOURED}
								className={classes.img}
								alt="Unable to process booking stage 1."
							/>
						</div>
						<Typography
							variant="body1"
							component="p"
							align="center"
						>
							{errorMessage}
						</Typography>
					</React.Fragment>
				}
				actions={
					<React.Fragment>
						<Button
							onClick={() => {
								window.open(`tel:${QV_MEDICAL_PHONE_NUMBER}`);
							}}
							color="primary"
							variant="contained"
						>
							Call
						</Button>
					</React.Fragment>
				}
			/>
		)
	}

	const handleNextButtonClick = async () => {
		setLoading(true);
		try {
			const apptReq = AppointmentBookingRequest.parsePatientDetails(
				patientDetails,
				medicareCard,
				concessionCard,
				appointmentBookingRequest
			);

			const validationResult = AppointmentBookingRequest.schema.validate(
				apptReq
			);

			const { error } = validationResult;
			if (error) {
				throw new Error(error.message);
			}
			handleAppointmentBookingRequestChange(apptReq);
			try {
				const result = await AppointmentBookingRequest.startBookingStageOne(
					apptReq
				);
				setBookingCookies(result.data, apptReq);
			} catch (error) {
				throw new UnableToProcessBookingStageOneError();
			}
			handleJourneyStageChange(Journey.goFrom(journeyStage, "forward"));
		} catch (error) {
			console.log(error);
			//setError(error.message);
		}
		setLoading(false);
	}

	return (
		<StyledSectionContainer
			ref={ref}
			className={classes.root}
			maxWidth="xs"
		>
			{trail.map(({ x, opacity, ...rest }, index) => {
				const Comp = components[index];

				return (
					<Comp
						key={index}
						style={{
							opacity,
							transform: x.interpolate(
								(x) => `translateX(${x}px)`
							),
							...rest,
						}}
					/>
				);
			})}
			<Grid container spacing={2} style={{ marginTop: 8 }}>
				<Grid item xs={12}>
					{/* <StyledRequestVerificationCodeButton /> */}
					<LoadingButton
						fullWidth
						onClick={handleNextButtonClick}
						isLoading={loading}
						disabled={loading || isValidationFailed()}
					>
						Next
					</LoadingButton>
				</Grid>
			</Grid>
		</StyledSectionContainer>
	);
});

const StyledHealthCoverDetailsSection = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("HealthCoverDetailsSection"),
})(HealthCoverDetailsSection);

export default StyledHealthCoverDetailsSection;

export const AnimatedHealthCoverDetailsSection = animated(
	StyledHealthCoverDetailsSection
);
